/**
 * Theme stickers: round cosmetic badges earned from opt-in rewarded ads
 * (docs/MONETIZATION.md § 2). Each is painted from its theme's own palette —
 * a sky, a hill of that theme's bricks and the level number — so every theme
 * gets a distinct sticker without any image files.
 */
import { THEMES, type Locale, type ThemePack } from '@dropstack/core';
import { useId, type CSSProperties } from 'react';
import { useT } from '../i18n/index.ts';
import './ui.css';

export interface StickerProps {
  theme: ThemePack;
  collected: boolean;
  locale: Locale;
}

export function Sticker({ theme, collected, locale }: StickerProps) {
  const t = useT();
  const skyId = useId();
  const p = theme.palette;
  const style = {
    '--st-sky-top': p.skyTop,
    '--st-sky-bottom': p.skyBottom,
    '--st-hill': p.brickA,
    '--st-hill-2': p.brickB,
    '--st-accent': p.accent,
    '--st-text': p.blockText,
  } as CSSProperties;
  return (
    <li className={collected ? 'ui-sticker' : 'ui-sticker ui-sticker--locked'} style={collected ? style : undefined}>
      <span className="ui-sticker__art" aria-hidden="true">
        {collected ? (
          <svg viewBox="0 0 64 64" focusable="false">
            <defs>
              <linearGradient id={skyId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" style={{ stopColor: 'var(--st-sky-top)' }} />
                <stop offset="1" style={{ stopColor: 'var(--st-sky-bottom)' }} />
              </linearGradient>
            </defs>
            <circle cx="32" cy="32" r="29" className="ui-sticker__sky" fill={`url(#${skyId})`} />
            <circle cx="46" cy="20" r="7" className="ui-sticker__sun" />
            <path d="M3.5 40c9-8 17-10 26-6s18 3 31-4v6a29 29 0 0 1-57 4Z" className="ui-sticker__hill2" />
            <path d="M3.8 44c10-5 19-6 29-2s17 2 27-1a29 29 0 0 1-56 3Z" className="ui-sticker__hill" />
            <text x="32" y="36" textAnchor="middle" className="ui-sticker__level">
              {theme.level}
            </text>
          </svg>
        ) : (
          <svg viewBox="0 0 64 64" focusable="false">
            <circle cx="32" cy="32" r="29" className="ui-sticker__blank" />
            <text x="32" y="41" textAnchor="middle" className="ui-sticker__unknown">
              ?
            </text>
          </svg>
        )}
      </span>
      {collected ? (
        <span className="ui-sticker__name">{theme.name[locale]}</span>
      ) : (
        <span className="ui-sticker__name">
          <span aria-hidden="true">{theme.level}</span>
          <span className="visually-hidden">{t('shell.stickerLocked', { level: theme.level })}</span>
        </span>
      )}
    </li>
  );
}

export interface StickerShelfProps {
  collected: readonly string[];
  locale: Locale;
  /** Accessible name of the list. */
  label: string;
}

/** Every theme's sticker slot, collected ones in colour. */
export function StickerShelf({ collected, locale, label }: StickerShelfProps) {
  return (
    <ul className="ui-sticker-shelf" aria-label={label}>
      {THEMES.map((theme) => (
        <Sticker key={theme.id} theme={theme} collected={collected.includes(theme.id)} locale={locale} />
      ))}
    </ul>
  );
}
